import compact from 'lodash/compact'
import isEqual from 'lodash/isEqual'
import isFunction from 'lodash/isFunction'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { CheckoutModalContext } from '@Domains/Checkout/Providers/CheckoutModalProvider'
import { RecaptchaContext } from '@Domains/Checkout/Providers/RecaptchaContextProvider'
import { PaymentError } from '@Domains/Checkout/Steps/Payment/Components/PaymentError/PaymentError'
import {
  CheckoutFlowModel,
  CheckoutFlowNavigationStep,
  CheckoutFlowStep,
  CheckoutFlowSteps,
  PaymentSubmit,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { COD_PAYMENT, FREE_PAYMENT } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowStepValidation'
import { useFinalizeCartMutation } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

export const useCheckoutFlowSubmit = (
  cartID: string,
  cartChecksum: string,
  model: CheckoutFlowModel,
  stepValidations: Record<CheckoutFlowStep, boolean>,
  setActiveStep: (step: CheckoutFlowNavigationStep) => void,
  hasHadError: boolean,
  subscribeToClub: boolean,
  subscribeToCommercial: boolean,
) => {
  const { t } = useTranslation()
  const finalizedIDs = useRef<string[]>([])
  const {
    isShown,
    setModal,
    unSetModal,
  } = useContext(CheckoutModalContext)
  const [submitPayment, { loading: finalizeLoading }] = useFinalizeCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const validRef = useRef(false)
  const [loading, setLoading] = useState(false)
  const loadingRef = useRef(false)
  const recaptcha = useContext(RecaptchaContext)
  const checkoutPromptsV1 = useSplitTest('checkout_prompts_v1', 'b')

  // submitCheckout is used by external components to provide data if everything is valid we use the ref
  useEffect(() => {
    validRef.current = stepValidations.ADDRESS && stepValidations.SHIPPING
  }, [stepValidations])

  useEffect(() => {
    if (!isShown) {
      loadingRef.current = false
      setLoading(false)
    }
  }, [isShown])

  const submitCheckout: PaymentSubmit = useCallback(async (data, callback) => {
    if (finalizeLoading) {
      return
    }
    if (finalizedIDs.current.includes(cartID)) {
      ampli.cofCompletionFailRes({
        payment_method: model.paymentStep.payment?.type,
      })
      setModal(<PaymentError
        closeModal={() => unSetModal()}
        error={t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')}
      />)
      return
    }
    if (validRef.current && !loadingRef.current && !loading) {
      loadingRef.current = true
      setLoading(true)
      if (typeof document !== 'undefined' && document.activeElement && 'blur' in document.activeElement && isFunction(document.activeElement.blur)) {
        document.activeElement.blur()
      }
      try {
        let recaptchaToken = ''
        if (recaptcha) {
          try {
            recaptchaToken = await recaptcha.executeRecaptcha('submit_checkout')
          } catch (e) {
            console.error('recaptcha error', e)
          }
        }

        ampli.cofCompletionAttempt({
          has_had_payment_error: hasHadError,
          // @ts-ignore
          payment_method_type: model.paymentStep?.payment?.type || '',
          payment_option_id: model.paymentStep?.payment?.id || '',
          payment_provider: model.paymentStep?.payment?.provider || '',
        })
        const isFree = isEqual(FREE_PAYMENT, data)
        const isCOD = isEqual(COD_PAYMENT, data)
        if (!checkoutPromptsV1) {
          finalizedIDs.current = [...finalizedIDs.current, cartID]
        }
        let submitPaymentData
        if (isFree) {
          submitPaymentData = await submitPayment({
            variables: {
              checksum: cartChecksum,
              consents: compact([subscribeToClub ? 'club' : undefined, subscribeToCommercial ? 'commercial' : undefined]),
              paymentMethodID: undefined,
              paymentPayload: undefined,
              recaptchaToken,
            },
          })
        } else if (isCOD) {
          submitPaymentData = await submitPayment({
            variables: {
              checksum: cartChecksum,
              consents: compact([subscribeToClub ? 'club' : undefined, subscribeToCommercial ? 'commercial' : undefined]),
              paymentMethodID: COD_PAYMENT.id,
              paymentPayload: {},
              recaptchaToken,
            },
          })
        } else {
          submitPaymentData = await submitPayment({
            variables: {
              checksum: cartChecksum,
              consents: compact([subscribeToClub ? 'club' : undefined, subscribeToCommercial ? 'commercial' : undefined]),
              paymentMethodID: model.paymentStep.payment?.id,
              paymentPayload: data as any,
              recaptchaToken,
            },
          })
        }
        const handled = callback(submitPaymentData.data?.finalizeCart)
        if (!handled) {
          throw new Error('Frontend handled card error')
        }
      } catch (e) {
        ampli.cofCompletionFailRes({
          payment_method: model.paymentStep.payment?.type,
        })

        finalizedIDs.current = finalizedIDs.current.filter(id => id !== cartID)

        setModal(<PaymentError
          closeModal={() => unSetModal()}
          error={t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')}
        />)
        console.warn('Create Return Error:', e)
      }
    }
    setActiveStep(CheckoutFlowSteps.SUCCESS)
  }, [
    finalizeLoading,
    cartID,
    cartChecksum,
    loading,
    setActiveStep,
    model.paymentStep.payment?.type,
    model.paymentStep.payment?.id,
    model.paymentStep.payment?.provider,
    setModal,
    t,
    unSetModal,
    hasHadError,
    subscribeToClub,
    subscribeToCommercial,
    submitPayment,
    recaptcha,
    checkoutPromptsV1,
  ])
  return {
    submitCheckout,
    loading,
  }
}
